// src/components/ui/Card.js
import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

const CardWrapper = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  transition: all ${props => props.theme.transitions.medium};
  height: ${props => props.fullHeight ? '100%' : 'auto'};
  display: flex;
  flex-direction: column;
  border: ${props => props.border ? '1px solid #f0f0f0' : 'none'};
  
  ${props => props.hoverable && css`
    &:hover {
      transform: translateY(-8px);
      box-shadow: ${props.theme.shadows.medium};
    }
  `}
`;

const CardImage = styled.div`
  height: ${props => props.height || '200px'};
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
    
    ${props => props.hoverable && css`
      &:hover {
        transform: scale(1.05);
      }
    `}
  }
`;

const CardContent = styled.div`
  padding: ${props => props.theme.spacing.md};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: ${props => props.spacing || props.theme.spacing.sm};
  color: ${props => props.theme.colors.textMain};
  
  ${props => props.as === 'a' && css`
    color: ${props.theme.colors.textMain};
    text-decoration: none;
    transition: color ${props.theme.transitions.fast};
    
    &:hover {
      color: ${props.theme.colors.primary};
    }
  `}
`;

const CardText = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.spacing || props.theme.spacing.md};
  
  ${props => props.limit && css`
    display: -webkit-box;
    -webkit-line-clamp: ${props.limit};
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

const CardFooter = styled.div`
  padding: ${props => props.noPadding ? '0' : `0 ${props.theme.spacing.md} ${props.theme.spacing.md}`};
  margin-top: auto;
`;

const CardCategoryTag = styled.span`
  display: inline-block;
  background-color: ${props => props.theme.colors.primaryLight};
  color: ${props => props.theme.colors.light};
  font-size: ${props => props.theme.fontSizes.small};
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.sm};
  font-weight: 600;
`;

const CardDateTag = styled.span`
  display: block;
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: 600;
`;

const Card = ({
    children,
    image,
    imageHeight,
    imageAlt,
    title,
    titleLink,
    titleSpacing,
    text,
    textLimit,
    textSpacing,
    category,
    date,
    footer,
    footerNoPadding,
    hoverable = true,
    fullHeight = false,
    border = false,
    ...rest
}) => {
    return (
        <CardWrapper
            hoverable={hoverable}
            fullHeight={fullHeight}
            border={border}
            {...rest}
        >
            {image && (
                <CardImage height={imageHeight} hoverable={hoverable}>
                    <img src={image} alt={imageAlt || title} />
                </CardImage>
            )}

            <CardContent>
                {category && <CardCategoryTag>{category}</CardCategoryTag>}
                {date && <CardDateTag>{date}</CardDateTag>}

                {title && (
                    titleLink ? (
                        <CardTitle as={Link} to={titleLink} spacing={titleSpacing}>{title}</CardTitle>
                    ) : (
                        <CardTitle spacing={titleSpacing}>{title}</CardTitle>
                    )
                )}

                {text && <CardText limit={textLimit} spacing={textSpacing}>{text}</CardText>}

                {children}
            </CardContent>

            {footer && <CardFooter noPadding={footerNoPadding}>{footer}</CardFooter>}
        </CardWrapper>
    );
};

export default Card;